/**
 * GLAMRDiP Instant Gels builder — V2 (redesign preview).
 *
 * PRESENTATION ONLY. The cart payload below is copied verbatim from
 * assets/instant-gels-builder.js handleAtc(), including the lock-step guard that
 * refuses to add unless the selection EXACTLY equals a tier's design count. That
 * guard is the revenue backstop — it is why a 5-design selection can never reach
 * the cart at the 4-pack price. Do not "simplify" it.
 *
 * Difference from v1 that is NOT cosmetic: v1 is count-driven (pick designs, the
 * tier follows). This design is tier-first (pick 4/6/8, then fill that many slots).
 * Selecting a smaller tier truncates the selection, matching the mock.
 */
(() => {
  'use strict';

  const boot = () => {
    document.querySelectorAll('[data-gd-igb2]').forEach(mount);
  };

  function mount(host) {
    const dataEl = host.querySelector('[data-gd-igb2-data]');
    const root = host.querySelector('[data-gd-igb2-root]');
    if (!dataEl || !root) return;

    let DATA;
    try { DATA = JSON.parse(dataEl.textContent); } catch (e) { console.warn('[igb2] bad data', e); return; }
    const TIERS = (DATA.tiers || []).filter(t => t && t.count > 0).sort((a, b) => a.count - b.count);
    // FIX (Claude 2026-10-04): the collection is sorted best-selling, so a sold-out
    // former bestseller sat 2nd in the grid. Sold-out designs now go LAST, once, here —
    // every view (chips, grid) filters this array, and selection/detail/tray/cart all key
    // on d.id (the Liquid forloop index), never on array position, so nothing else moves.
    // Two filters + concat rather than sort(): stable by construction, original order kept
    // within each group. `!d.available` is the same test the grid uses for its badge.
    const DESIGNS = (all => all.filter(d => d.available).concat(all.filter(d => !d.available)))(
      (DATA.designs || []).filter(d => d && d.variantId));
    const L = DATA.labels || {};
    // Placeholder-bearing templates can't carry a Liquid default (a brace inside a
    // quoted default terminates the output tag), so their fallbacks live here.
    const FALLBACK = {
      ctaRemaining: 'Velg {n} design til',
      selected: '{n} av {total} valgt',
      upsell: 'Oppgrader til {n} design — bare {d} kr mer ({p} kr/design)',
      upsellBtn: 'Bytt til {n}',
    };
    Object.keys(FALLBACK).forEach(k => {
      if (!L[k] || !String(L[k]).trim()) L[k] = FALLBACK[k];
    });
    const AVATARS = (DATA.trustAvatars || []).filter(Boolean);
    if (!TIERS.length || !DESIGNS.length) return;

    TIERS.forEach(t => { t.extras = (t.extras || []).filter(Boolean); });

    // FIX (Claude 2026-08-07): ' kr' was hardcoded, so the Finnish (EUR) store printed
    // "1 099 kr". The section already emits data-currency — use it. Minor units appear
    // only when the amount has them: Nordic prices are whole units, EUR often is not.
    // FIX (Claude 2026-08-07, final): the ONLY defect in the original formatter was the
    // hardcoded ' kr', which printed kroner on the Finnish euro store. Space-grouping is
    // correct for all four Nordic markets and matches what this storefront already renders
    // ("1 099 kr"), so it stays. Attempts to derive the format from money_format or from
    // Shopify's `| money` filter BOTH produced periods/commas that disagree with the page.
    // The unit is now a per-market setting, like every other string in this section.
    // FIX (Claude 2026-10-04): two defects in the line above. (1) Rounding to whole units
    // showed FI's 99,90 € kit as "100€" — ABOVE the charged price. (2) Shopify strips
    // leading spaces from text settings, so the ' kr' suffix arrives as 'kr' and NO
    // printed "1 099kr". Now: space-grouped whole units; ",cc" only when the amount has
    // cents; exactly one no-break space before the trimmed suffix -> "1 099 kr", "99,90 €",
    // "110 €". amount() is the number alone, for templates that carry their own unit.
    const SUFFIX = String(L.currencySuffix || 'kr').trim();
    const amount = c => {
      const t = Math.round(Math.abs(+c || 0)), cents = t % 100;
      return (c < 0 && t ? '-' : '') + String((t - cents) / 100).replace(/\B(?=(\d{3})+(?!\d))/g, '\u00A0')
        + (cents ? ',' + String(cents).padStart(2, '0') : '');
    };
    const money = c => amount(c) + (SUFFIX ? '\u00A0' + SUFFIX : '');
    // Whole-unit rounding, kept ONLY for the derived per-design average (price ÷ count),
    // which is never a charged amount. Every charged amount goes through money()/amount().
    const bare = c => Math.round((c || 0) / 100);
    const tpl = (s, map) => String(s || '').replace(/\{(\w+)\}/g, (_, k) => (k in map ? map[k] : ''));

    const state = { tier: TIERS[0], selected: [], chip: '__all', expanded: null, gal: 0, busy: false };

    // ---- chips are built from the metafields the products actually carry, so a
    // ---- market with different data simply gets fewer chips rather than empty ones.
    // Badge membership comes from theme settings (product pickers), matched by name.
    const BEST = new Set(DATA.bestsellers || []);
    const NEWD = new Set(DATA.newDesigns || []);
    DESIGNS.forEach(d => { d.best = BEST.has(d.name); d.isNew = NEWD.has(d.name); });

    const uniq = arr => [...new Set(arr.filter(Boolean))];
    const CHIPS = [{ key: '__all', label: L.all || 'Alle', test: () => true }];
    // Badge chips sit right after "Alle" — merchandised groups before attributes.
    if (DESIGNS.some(d => d.best)) CHIPS.push({ key: '__best', label: L.bestseller || 'Bestselger', test: d => d.best });
    if (DESIGNS.some(d => d.isNew)) CHIPS.push({ key: '__new', label: L.newBadge || 'Nyhet', test: d => d.isNew });
    uniq(DESIGNS.flatMap(d => d.design || [])).sort().forEach(v =>
      CHIPS.push({ key: 'd:' + v, label: v, test: d => (d.design || []).includes(v) }));
    uniq(DESIGNS.map(d => d.colour)).sort().forEach(v =>
      CHIPS.push({ key: 'c:' + v, label: v, test: d => d.colour === v }));
    uniq(DESIGNS.map(d => d.form)).sort().forEach(v =>
      CHIPS.push({ key: 'f:' + v, label: cap(v), test: d => d.form === v }));
    uniq(DESIGNS.map(d => d.len)).sort().forEach(v =>
      CHIPS.push({ key: 'l:' + v, label: cap(v), test: d => d.len === v }));

    function cap(s) { s = String(s || ''); return s.charAt(0).toUpperCase() + s.slice(1); }
    function esc(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])); }

    // ---------------- shell ----------------
    root.innerHTML = `
      <div class="gd-igb2__backdrop" data-close></div>
      <div class="gd-igb2__panel" role="dialog" aria-modal="true" aria-label="${esc(L.heading)}">
        <header class="gd-igb2__head">
          <button type="button" class="gd-igb2__circ" data-close aria-label="${esc(L.back)}">&lsaquo;</button>
          <h2 class="gd-igb2__title">${esc(L.heading)}</h2>
          <button type="button" class="gd-igb2__circ" data-close aria-label="${esc(L.close)}">&#10005;</button>
        </header>
        <div class="gd-igb2__body">
          <div class="gd-igb2__main">
            <div class="gd-igb2__chipbar">
              <button type="button" class="gd-igb2__chipnav is-prev" data-chipnav="-1" aria-label="&lsaquo;" hidden>&lsaquo;</button>
              <div class="gd-igb2__chips" data-chips></div>
              <button type="button" class="gd-igb2__chipnav is-next" data-chipnav="1" aria-label="&rsaquo;" hidden>&rsaquo;</button>
            </div>
            <div class="gd-igb2__gridwrap"><div class="gd-igb2__grid" data-grid></div></div>
            <div class="gd-igb2__trust" data-trust></div>
            <!-- Detail lives INSIDE the main column so on desktop it takes over the
                 grid pane while the tier rail stays visible — matching the current
                 builder's is-quickview pane swap. On mobile CSS promotes it to a
                 fullscreen overlay, which is what the mock specifies there. -->
            <div class="gd-igb2__detail" data-detail hidden></div>
          </div>
          <aside class="gd-igb2__rail" data-rail></aside>
        </div>
        <div class="gd-igb2__toast" data-toast hidden></div>
      </div>`;

    const $ = s => root.querySelector(s);
    const elChips = $('[data-chips]'), elGrid = $('[data-grid]'), elRail = $('[data-rail]'),
          elTrust = $('[data-trust]'), elDetail = $('[data-detail]'), elToast = $('[data-toast]');

    // Desktop shows avatars first; the mock's mobile bar leads with the guarantee and
    // swaps the avatars for a heart. Same markup both places — CSS picks the variant.
    function trustHtml() {
      return `
      <span class="gd-igb2__trust-item gd-igb2__trust-item--customers">
        ${AVATARS.map(a => `<img src="${esc(a)}" alt="" class="gd-igb2__av">`).join('')}
        ${heart()}<b>${esc(L.trustCustomers)}</b></span>
      <span class="gd-igb2__trust-item gd-igb2__trust-item--guarantee">${shield()}<b>${esc(L.trustGuarantee)}</b></span>
      <span class="gd-igb2__trust-item gd-igb2__trust-item--shipping">${truck()}<b>${esc(L.trustShipping)}</b></span>`;
    }
    function heart() { return '<svg class="gd-igb2__heart" width="12" height="12" viewBox="0 0 24 24" fill="currentColor" stroke="none"><path d="M12 21s-7.5-4.9-10-9.3C.4 8.6 2.3 5 5.7 5c2 0 3.4 1.1 4.3 2.6C10.9 6.1 12.3 5 14.3 5c3.4 0 5.3 3.6 3.7 6.7C15.5 16.1 12 21 12 21z"/></svg>'; }
    elTrust.innerHTML = trustHtml();

    function shield() { return '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2l7 3v6c0 4.8-3.2 8.4-7 10-3.8-1.6-7-5.2-7-10V5l7-3z"/><path d="M9 12l2 2 4-4"/></svg>'; }
    function truck() { return '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M1 7h12v9H1z"/><path d="M13 10h4l3 3v3h-7"/><circle cx="5.5" cy="18" r="1.6"/><circle cx="16.5" cy="18" r="1.6"/></svg>'; }

    let toastTimer = null;
    function flash(msg) {
      clearTimeout(toastTimer);
      elToast.textContent = msg; elToast.hidden = false;
      toastTimer = setTimeout(() => { elToast.hidden = true; }, 2400);
    }

    // ---------------- open / close ----------------
    const openBtns = host.querySelectorAll('[data-gd-igb2-open]');
    function open() {
      root.hidden = false;
      document.documentElement.classList.add('gd-igb2-locked');
      if (window.gdScrollLock) window.gdScrollLock.lock(root);
      render();
    }
    function close() {
      // Remove offer (gd-upsell-modal.js) mounted in the panel: settle it before hiding,
      // or its promise and key listener would hang in a hidden window. Claude 2026-10-07.
      const liveOffer = root.querySelector('.gd-upsell:not(.gd-upsell--leaving)');
      if (liveOffer) liveOffer.dispatchEvent(new CustomEvent('gd:upsell:close', { bubbles: true }));
      root.hidden = true;
      document.documentElement.classList.remove('gd-igb2-locked');
      if (window.gdScrollLock) window.gdScrollLock.unlock(root);
    }
    openBtns.forEach(b => b.addEventListener('click', open));

    // Optional takeover (draft evaluation): make the page's existing
    // "Build your kit" CTA open this builder instead of v1. Capture phase +
    // stopImmediatePropagation so v1's own handler never runs — otherwise both
    // modals would stack. Only active when the section setting is on.
    if (host.dataset.takeover === 'true') {
      document.addEventListener('click', e => {
        const t = e.target.closest && e.target.closest('[data-gd-open-bundle-builder]');
        if (!t) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        open();
      }, true);
    }
    root.addEventListener('click', e => { if (e.target.closest('[data-close]')) close(); });
    document.addEventListener('keydown', e => {
      if (e.key !== 'Escape' || root.hidden) return;
      if (state.expanded != null) { state.expanded = null; render(); } else close();
    });

    // ---------------- selection ----------------
    function pickTier(t) {
      state.tier = t;
      if (state.selected.length > t.count) state.selected = state.selected.slice(0, t.count);
      render();
    }
    function toggle(id) {
      const i = state.selected.indexOf(id);
      if (i >= 0) { state.selected.splice(i, 1); render(); return true; }
      // FIX (Claude 2026-08-07): a sold-out or retired design must never enter the kit.
      // /cart/add.js with items[] is ALL-OR-NOTHING, so one unavailable design fails the
      // whole add — the shopper loses every pick and the free extras, with nothing marking
      // which card broke it. Reproduced live with "Chrome Kisses".
      const dsn = DESIGNS.find(x => x.id === id);
      if (!dsn || dsn.available === false) {
        flash((dsn ? dsn.name + ' — ' : '') + (L.soldOut || 'Sold out'));
        return false;
      }
      if (state.selected.length >= state.tier.count) { flash(L.kitFull); return false; }
      state.selected.push(id);
      render();
      return true;
    }

    // ---------------- render ----------------
    function render() {
      const tier = state.tier, picked = state.selected.length, remaining = tier.count - picked;

      elChips.innerHTML = CHIPS.map(c =>
        `<button type="button" class="gd-igb2__chip${c.key === state.chip ? ' is-active' : ''}" data-chip="${esc(c.key)}">${esc(c.label)}</button>`).join('');

      // Synchronously first — reading scrollWidth forces layout, so this is correct
      // immediately and doesn't depend on rAF (which never fires in a background
      // tab). The rAF pass afterwards catches late web-font reflow.
      syncChipNav();
      requestAnimationFrame(syncChipNav);

      const active = CHIPS.find(c => c.key === state.chip) || CHIPS[0];
      const list = DESIGNS.filter(active.test);
      elGrid.innerHTML = list.map(d => {
        const idx = state.selected.indexOf(d.id), sel = idx >= 0;
        return `<article class="gd-igb2__card${sel ? ' is-selected' : ''}${d.available ? '' : ' is-oos'}" data-card="${d.id}">
          <div class="gd-igb2__thumb">
            ${d.img ? `<img src="${esc(d.img)}" alt="${esc(d.name)}" loading="lazy">` : ''}
            ${!d.available ? `<span class="gd-igb2__oos">${esc(L.soldOut)}</span>`
              : d.best ? `<span class="gd-igb2__badge is-best">${esc(L.bestseller)}</span>`
              : d.isNew ? `<span class="gd-igb2__badge is-new">${esc(L.newBadge)}</span>` : ''}
            <button type="button" class="gd-igb2__add${sel ? ' is-on' : ''}" data-add="${d.id}"
              ${d.available === false ? 'disabled aria-disabled="true"' : ''}
              aria-label="${esc(d.name)}">${sel ? '✓ ' + (idx + 1) : '+'}</button>
          </div>
          <div class="gd-igb2__meta">
            <div class="gd-igb2__name">${esc(d.name)}</div>
            <div class="gd-igb2__sub">${esc([cap(d.len), cap(d.form), (d.design || [])[0]].filter(Boolean).join(' · '))}</div>
          </div>
        </article>`;
      }).join('');

      const slots = Array.from({ length: tier.count }, (_, i) => {
        const id = state.selected[i], d = DESIGNS.find(x => x.id === id);
        return d
          ? `<div class="gd-igb2__slot is-filled" data-slot="${d.id}" style="background-image:url('${esc(d.img)}')">
               <button type="button" class="gd-igb2__slot-x" data-unpick="${d.id}" aria-label="${esc(L.close)}">&#10005;</button></div>`
          : `<div class="gd-igb2__slot"><span>${i + 1}</span></div>`;
      }).join('');

      const nextTier = TIERS.find(t => t.count > tier.count);
      const showUpsell = remaining === 0 && !!nextTier;

      // The rail is split into a TOP piece (size choice) and a BOTTOM piece (kit
      // summary + CTA). On desktop both are display:contents so the rail reads as
      // one column, exactly as before. On mobile the rail itself goes
      // display:contents and these two pieces sit above and below the grid —
      // which is the arrangement in the mock.
      elRail.innerHTML = `
        <div class="gd-igb2__rail-top">
        <div class="gd-igb2__step">1 · ${esc(L.step1)}</div>
        <div class="gd-igb2__tiers">
          ${TIERS.map((t, i) => {
            const on = t.count === tier.count;
            // FIX (Claude 2026-10-04): "popular" was hardcoded to index 1 (6 designs), but
            // the 4-design kit is 79-94% of kit sales — it goes on the FIRST tier now; "best
            // price" stays on the last. Both need a comparison, so a single tier gets
            // neither, and with two tiers they land on different pills.
            const hot = TIERS.length > 1 && i === 0;
            const tag = hot ? L.popular : (TIERS.length > 1 && i === TIERS.length - 1 ? L.bestPrice : '');
            return `<button type="button" class="gd-igb2__tier${on ? ' is-active' : ''}" data-tier="${t.count}">
              <span class="gd-igb2__dot"></span>
              <span class="gd-igb2__tier-txt">
                <span class="gd-igb2__tier-top">${t.count} ${esc(L.unit || 'design')}
                  ${tag ? `<em class="gd-igb2__tag${hot ? ' is-hot' : ''}">${esc(tag)}</em>` : ''}</span>
                <span class="gd-igb2__tier-per">${bare(Math.round(t.price / t.count))} ${esc(L.perDesign)}</span>
              </span>
              <span class="gd-igb2__tier-price">
                <b>${money(t.price)}</b>
                ${t.compareAt > t.price ? `<s>${money(t.compareAt)}</s>` : ''}
              </span></button>`;
          }).join('')}
        </div>
        </div>
        <div class="gd-igb2__rail-bottom">
        <div class="gd-igb2__step">2 · ${esc(L.step2)}</div>
        <div class="gd-igb2__slots">${slots}</div>
        <div class="gd-igb2__status">${esc(tpl(L.selected, { n: picked, total: tier.count }))}</div>
        <div class="gd-igb2__bar"><i style="width:${(picked / tier.count) * 100}%"></i></div>
        <!-- Mobile-only summary line: "Ditt kit · 2/4" with the price right-aligned.
             Desktop keeps the separate status line + large price in the foot. -->
        <div class="gd-igb2__kitline">
          <span>${esc(L.kitLabel || 'Ditt kit')} · ${picked}/${tier.count}</span>
          <span class="gd-igb2__kitline-price">
            <b>${money(tier.price)}</b>
            ${tier.compareAt > tier.price ? `<s>${money(tier.compareAt)}</s>` : ''}
          </span>
        </div>
        ${showUpsell ? `<div class="gd-igb2__upsell">
            <p>✦ ${esc(tpl(L.upsell, { n: nextTier.count, d: amount(nextTier.price - tier.price), p: bare(Math.round(nextTier.price / nextTier.count)) }))}</p>
            <button type="button" data-upsell="${nextTier.count}">${esc(tpl(L.upsellBtn, { n: nextTier.count }))}</button>
          </div>` : ''}
        <div class="gd-igb2__foot">
          <div class="gd-igb2__price">
            <b>${money(tier.price)}</b>
            ${tier.compareAt > tier.price ? `<s>${money(tier.compareAt)}</s>
              <em>${esc(L.save)} ${money(tier.compareAt - tier.price)}</em>` : ''}
          </div>
          <button type="button" class="gd-igb2__cta${remaining === 0 ? ' is-ready' : ''}" data-atc
            ${remaining === 0 && !state.busy ? '' : 'disabled'}>
            ${state.busy ? esc(L.adding) : (remaining === 0 ? esc(L.ctaReady) + ' — ' + money(tier.price) : esc(tpl(L.ctaRemaining, { n: remaining })))}
          </button>
          ${(tier.extras || []).length
            ? `<div class="gd-igb2__incl">${esc(tier.count <= TIERS[0].count ? L.includes4 : L.includes68)}</div>`
            : ''}
          <div class="gd-igb2__trust gd-igb2__trust--m">${trustHtml()}</div>
        </div>
        </div>`;

      renderDetail();
    }

    const panelEl = root.querySelector('.gd-igb2__panel');
    function renderDetail() {
      // Drives the pane swap: with .is-detail on the panel, CSS hides the chips/grid
      // on desktop so the detail owns the left pane and the rail stays usable.
      panelEl.classList.toggle('is-detail', state.expanded != null);
      if (state.expanded == null) { elDetail.hidden = true; elDetail.innerHTML = ''; return; }
      const d = DESIGNS.find(x => x.id === state.expanded);
      if (!d) { elDetail.hidden = true; return; }
      const gal = (d.gallery && d.gallery.length ? d.gallery : [d.img]).filter(Boolean);
      const gi = ((state.gal % gal.length) + gal.length) % gal.length;
      // Gallery URLs come from the metafield unsized, so ask the CDN for a sensible width.
      const sized = (u, w) => (!u || /[?&]width=/.test(u)) ? u : u + (u.indexOf('?') >= 0 ? '&' : '?') + 'width=' + w;
      // Warm the neighbours so paging through feels instant.
      if (gal.length > 1) {
        [gal[(gi + 1) % gal.length], gal[(gi - 1 + gal.length) % gal.length]]
          .forEach(u => { const im = new Image(); im.src = sized(u, 900); });
      }
      const sel = state.selected.includes(d.id);
      const rows = [
        [L.specLen, cap(d.len)], [L.specForm, cap(d.form)], [L.specCover, d.cover],
        [L.specDesign, (d.design || []).join(' · ')], [L.specColour, d.colour],
      ].filter(r => r[1]);
      elDetail.hidden = false;
      elDetail.innerHTML = `
        <div class="gd-igb2__detail-bd" data-collapse></div>
        <div class="gd-igb2__detail-card">
          <div class="gd-igb2__galcol">
            <div class="gd-igb2__gal">
              <div class="gd-igb2__gtrack" data-gtrack>${gal.map((u, i) => `<span class="gd-igb2__gslide" style="background-image:url('${esc(sized(u, 900))}')" role="img" aria-label="${esc(d.name)} ${i + 1} / ${gal.length}"></span>`).join('')}</div>
              ${gal.length > 1 ? `<button type="button" class="gd-igb2__gnav is-prev" data-gal="-1">&lsaquo;</button>
                <button type="button" class="gd-igb2__gnav is-next" data-gal="1">&rsaquo;</button>
                <span class="gd-igb2__gcount">${gi + 1} / ${gal.length}</span>` : ''}
            </div>
            ${gal.length > 1 ? `<div class="gd-igb2__gthumbs">${gal.map((u, i) => `
              <button type="button" class="gd-igb2__gthumb${i === gi ? ' is-on' : ''}" data-galto="${i}"
                style="background-image:url('${esc(sized(u, 240))}')"
                aria-label="${esc(d.name)} ${i + 1}/${gal.length}"${i === gi ? ' aria-current="true"' : ''}></button>`).join('')}</div>` : ''}
          </div>
          <div class="gd-igb2__detail-body">
            <h3>${esc(d.name)}${d.best ? `<span class="gd-igb2__badge is-best is-inline">${esc(L.bestseller)}</span>`
              : d.isNew ? `<span class="gd-igb2__badge is-new is-inline">${esc(L.newBadge)}</span>` : ''}</h3>
            <dl class="gd-igb2__spec">
              ${rows.map(r => `<dt>${esc(r[0])}</dt><dd>${esc(r[1])}</dd>`).join('')}
            </dl>
            <p class="gd-igb2__note">${esc(L.setNote)}</p>
            <div class="gd-igb2__detail-actions">
              <button type="button" class="gd-igb2__cta${d.available === false ? '' : ' is-ready'}" data-add="${d.id}"
                ${d.available === false ? 'disabled aria-disabled="true"' : ''}>
                ${d.available === false ? esc(L.soldOut) : (sel ? esc(L.removeFromKit) : esc(L.addToKit))}</button>
              <button type="button" class="gd-igb2__ghost" data-collapse>${esc(L.keepBrowsing)}</button>
            </div>
          </div>
          <button type="button" class="gd-igb2__circ gd-igb2__detail-x" data-collapse aria-label="${esc(L.close)}">&#10005;</button>
        </div>`;
      bindTrack(gi);
    }

    // ---------------- detail photos: photo strip (salon kit pattern) ----------------
    // Claude 2026-10-07 (owner request): the detail photo was one background that the arrows swapped,
    // so a finger or a mouse could not move it. Every photo is now a slide in a horizontal scroll-snap
    // strip, like the salon kit builder: touch scrolls it natively (the photo follows the finger and
    // snaps), a desktop mouse drags it, arrows/thumbs glide it, and the counter + thumbs follow the
    // scroll. state.gal stays the index, so a re-render lands on the same photo.
    function galTrack() { return elDetail.querySelector('[data-gtrack]'); }
    function galSync(i) {
      const t = galTrack(), n = t ? t.children.length : 0;
      if (!n) return;
      state.gal = ((i % n) + n) % n;
      const c = elDetail.querySelector('.gd-igb2__gcount');
      if (c) c.textContent = `${state.gal + 1} / ${n}`;
      elDetail.querySelectorAll('.gd-igb2__gthumb').forEach((b, k) => {
        b.classList.toggle('is-on', k === state.gal);
        if (k === state.gal) b.setAttribute('aria-current', 'true'); else b.removeAttribute('aria-current');
      });
    }
    function galGo(i) {
      const t = galTrack(), n = t ? t.children.length : 0;
      if (!n) return;
      galSync(i);
      t.scrollTo({ left: state.gal * t.clientWidth, behavior: 'smooth' });
    }
    function bindTrack(gi) {
      const t = galTrack();
      if (!t) return;
      t.scrollLeft = gi * t.clientWidth;          // same photo after a re-render, no animation
      let raf = 0;
      t.addEventListener('scroll', () => {
        if (raf || galDrag) return;               // during a mouse drag the release decides
        raf = requestAnimationFrame(() => {
          raf = 0;
          const w = t.clientWidth, k = w ? Math.round(t.scrollLeft / w) : 0;
          if (w && k !== state.gal) galSync(k);
        });
      }, { passive: true });
    }
    // Desktop mouse drag — gd-drag-scroll.js rules: mouse only, 5px threshold, pointer capture only once
    // it is a real drag, no preventDefault on pointerdown (clicks keep working), the click right after a
    // drag is swallowed. Release: dragged past 15% of the width -> next/previous photo, else back.
    let galDrag = null;
    root.addEventListener('pointerdown', e => {
      if (e.pointerType !== 'mouse' || e.button !== 0 || e.target.closest('button')) return;
      const t = e.target.closest('[data-gtrack]');
      if (!t || t.children.length < 2) return;
      galDrag = { t, id: e.pointerId, x: e.clientX, last: e.clientX, left: t.scrollLeft, moved: false, raf: 0 };
    });
    document.addEventListener('pointermove', e => {
      const g = galDrag;
      if (!g || e.pointerId !== g.id) return;
      g.last = e.clientX;
      if (!g.moved) {
        if (Math.abs(g.last - g.x) <= 5) return;
        g.moved = true;
        try { g.t.setPointerCapture(g.id); } catch (_) {}
        g.t.classList.add('is-dragging');
      }
      e.preventDefault();                          // no text selection while dragging
      if (!g.raf) g.raf = requestAnimationFrame(() => { g.raf = 0; g.t.scrollLeft = g.left - (g.last - g.x); });
    });
    const galDragEnd = e => {
      const g = galDrag;
      if (!g || e.pointerId !== g.id) return;
      galDrag = null;
      if (!g.moved) return;
      try { g.t.releasePointerCapture(g.id); } catch (_) {}
      const w = g.t.clientWidth || 1, dx = g.last - g.x, from = Math.round(g.left / w);
      const k = Math.max(0, Math.min(g.t.children.length - 1, Math.abs(dx) > w * 0.15 ? from + (dx < 0 ? 1 : -1) : from));
      galGo(k);
      setTimeout(() => g.t.classList.remove('is-dragging'), 450);
      const up = e.timeStamp, swallow = ev => { if (ev.timeStamp - up <= 80) { ev.stopPropagation(); ev.preventDefault(); } };
      g.t.addEventListener('click', swallow, { capture: true, once: true });
      setTimeout(() => g.t.removeEventListener('click', swallow, true), 80);
    };
    document.addEventListener('pointerup', galDragEnd);
    document.addEventListener('pointercancel', galDragEnd);

    // ---------------- events ----------------
    // Chip row is a single scrolling line; arrows appear only when it actually
    // overflows, and hide again at each end. Keeps 26 chips from wrapping into a
    // three-row block that swamps the grid.
    const chipNavs = root.querySelectorAll('[data-chipnav]');
    function syncChipNav() {
      const max = elChips.scrollWidth - elChips.clientWidth;
      const overflowing = max > 4;
      chipNavs.forEach(b => {
        const dir = +b.dataset.chipnav;
        b.hidden = !overflowing || (dir < 0 ? elChips.scrollLeft <= 2 : elChips.scrollLeft >= max - 2);
      });
    }
    elChips.addEventListener('scroll', syncChipNav, { passive: true });
    window.addEventListener('resize', syncChipNav);
    // The row has no width until the modal is first shown (and none again while the
    // detail pane covers it), so a render-time measurement reads zero and hides both
    // arrows. Observing the element catches every moment it actually gains a size.
    if (window.ResizeObserver) new ResizeObserver(syncChipNav).observe(elChips);
    root.addEventListener('click', e => {
      const nav = e.target.closest('[data-chipnav]');
      if (nav) {
        elChips.scrollBy({ left: (+nav.dataset.chipnav) * elChips.clientWidth * 0.8, behavior: 'smooth' });
        return;
      }
      const chip = e.target.closest('[data-chip]');
      if (chip) { state.chip = chip.dataset.chip; state.expanded = null; render(); return; }

      const tierBtn = e.target.closest('[data-tier]');
      if (tierBtn) { pickTier(TIERS.find(t => t.count === +tierBtn.dataset.tier)); return; }

      const up = e.target.closest('[data-upsell]');
      if (up) { pickTier(TIERS.find(t => t.count === +up.dataset.upsell)); return; }

      const un = e.target.closest('[data-unpick]');
      if (un) { e.stopPropagation(); toggle(+un.dataset.unpick); return; }

      const add = e.target.closest('[data-add]');
      if (add) {
        e.stopPropagation();
        const id = +add.dataset.add;
        const wasDetail = state.expanded === id;
        if (toggle(id) && wasDetail) { state.expanded = null; render(); }
        return;
      }

      // Thumb strip jumps to an absolute index; the arrows step relatively. state.gal
      // is normalised with modulo at render, so an absolute index is safe here.
      const gt = e.target.closest('[data-galto]');
      if (gt) { galGo(+gt.dataset.galto); return; }
      const g = e.target.closest('[data-gal]');
      if (g) { galGo(state.gal + +g.dataset.gal); return; }

      if (e.target.closest('[data-collapse]')) { state.expanded = null; render(); return; }

      const card = e.target.closest('[data-card]');
      if (card) { state.expanded = +card.dataset.card; state.gal = 0; render(); return; }

      const slot = e.target.closest('[data-slot]');
      if (slot) { state.expanded = +slot.dataset.slot; state.gal = 0; render(); return; }

      if (e.target.closest('[data-atc]')) handleAtc();
    });

    // ---------------- cart ----------------
    // Copied from assets/instant-gels-builder.js — same payload, same guard.
    const uuid = () => (crypto && crypto.randomUUID ? crypto.randomUUID()
      : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
          const r = Math.random() * 16 | 0; return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16); }));

    function emit(name, payload) {
      try {
        document.dispatchEvent(new CustomEvent('gd:event', { detail: { name, payload } }));
        if (window.dataLayer && window.dataLayer.push) window.dataLayer.push({ event: name, ...payload });
      } catch (e) {}
    }

    // ---------------- Remove upsell (4-design kit) ----------------
    // gd-upsell-modal.js (the kit page's gd-upsell-modal section) owns the offer. It skips —
    // resolving at once — when the section is absent, the offer was declined this session
    // (cap scoped to 'instant_gels_kit'), or Remove's SKU is already in the cart (the 6/8 kits
    // include it). Owner rule: offer it only with the smallest kit (4 designs = Heal only).
    async function offerRemove(tier, bundleId) {
      const up = window.gdUpsell;
      if (!up || typeof up.maybeShow !== 'function' || root.hidden) return;
      if (!tier || tier.count !== TIERS[0].count) return;
      try {
        await ensureUpsellCss();
        await up.maybeShow({ bundle_id: bundleId, count: tier.count, tier_price_cents: tier.price },
          { host: panelEl, context: 'instant_gels_kit', isCurrent: () => !root.hidden });
      } catch (e) { console.warn('[igb2] Remove offer skipped:', e); }
    }
    // The section loads gd-upsell-modal.css on ATC-click selectors this builder's button does
    // not match (or after 8 s idle). Make sure it is in before the offer mounts, so it never
    // paints unstyled. Exact versioned URL from the section's <noscript> fallback.
    function ensureUpsellCss() {
      const has = [...document.styleSheets].some(s => s.href && s.href.indexOf('gd-upsell-modal.css') >= 0);
      if (has) return Promise.resolve();
      let link = document.querySelector('link[href*="gd-upsell-modal.css"]');
      if (!link) {
        const ns = [...document.querySelectorAll('noscript')].map(n => n.textContent || '')
          .find(t => t.indexOf('gd-upsell-modal.css') >= 0);
        const m = ns && ns.match(/href="([^"]*gd-upsell-modal\.css[^"]*)"/);
        if (!m) return Promise.resolve();
        link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = m[1];
        document.head.appendChild(link);
      }
      return new Promise(res => {
        link.addEventListener('load', res, { once: true });
        link.addEventListener('error', res, { once: true });
        setTimeout(res, 1500);
      });
    }

    async function handleAtc() {
      const tier = state.tier;
      const chosen = state.selected.map(id => DESIGNS.find(d => d.id === id)).filter(Boolean);
      // LOCK-STEP revenue backstop — identical to v1. Never add an intermediate-count bundle.
      if (!tier || chosen.length !== tier.count) return;
      if (state.busy) return;
      state.busy = true; render();

      const bundleId = uuid();
      const designsStr = chosen.map(d => d.name).join(',');
      const items = [
        { id: tier.variantId, quantity: 1, properties: { Designs: designsStr, __bundle_id: bundleId, _kit: 'true' } },
        ...chosen.map(d => ({ id: d.variantId, quantity: 1, properties: { __bundle_id: bundleId, _addon: 'true' } })),
        ...(tier.extras || []).filter(x => x && x.id).map(x => ({
          id: x.id, quantity: Math.max(1, parseInt(x.qty, 10) || 1), properties: { __bundle_id: bundleId, _addon: 'true' },
        })),
      ];
      emit('instant_gels_atc_click', { tier_id: tier.variantId, design_count: chosen.length, price: tier.price });
      try {
        const r = await fetch('/cart/add.js', {
          method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items }),
        });
        if (!r.ok) { const b = await r.json().catch(() => ({})); throw new Error(b.description || 'Cart add failed'); }
        emit('instant_gels_atc_success', { tier_id: tier.variantId });
        try {
          if (window._learnq && typeof window._learnq.push === 'function') {
            window._learnq.push(['track', 'Added to Cart', {
              $value: (tier.price || 0) / 100,
              AddedItemProductName: tier.title || 'Instant Gels Bundle',
              AddedItemQuantity: 1,
              BundleDesigns: chosen.map(d => d.name),
              BundleSlotCount: tier.count,
            }]);
          }
        } catch (e) {}
        // FIX (Claude 2026-08-07): reset BEFORE closing. v1 does `selected.length = 0`;
        // v2 did not, so reopening the builder showed a full 4/4 kit with a live CTA and
        // one tap added a SECOND bundle. Reproduced: 12 lines, 2 kit parents, 2 198 kr.
        state.selected = [];
        state.expanded = null;
        state.gal = 0;
        // Remove upsell (Claude 2026-10-07, owner request): the 4-design kit includes Heal
        // but not Remove, so offer Remove inside this panel BEFORE the cart drawer opens —
        // the salon kit's hand-off (kit-builder.js finishHandoff). Resolves at once when
        // there is nothing to offer; every outcome then continues exactly as before.
        await offerRemove(tier, bundleId);
        // Event parity with v1: product-added-to-cart THEN gd:cart-open, and no
        // cart:updated (v1 never sent one; two events = two reconcile passes across the
        // gift engines). gd:cart-open is synchronous, so the drawer still opens even if
        // the drawer's own listener dies on its awaited /cart.js fetch.
        document.body.dispatchEvent(new CustomEvent('product-added-to-cart'));
        document.body.dispatchEvent(new CustomEvent('gd:cart-open'));
        if (!root.hidden) close();
      } catch (err) {
        flash(err.message || 'Cart add failed');
        emit('instant_gels_atc_error', { message: err.message });
      } finally {
        state.busy = false; render();
      }
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
